import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["filterForm", "validationMessage", "clientHint", "filtersWrapper", "resultsFrame"]

  connect() {
    // Listen for turbo:frame-load to update URL without full page visit
    if (this.hasResultsFrameTarget) {
      this.frameLoadHandler = this.handleFrameLoad.bind(this)
      this.resultsFrameTarget.addEventListener("turbo:frame-load", this.frameLoadHandler)
    }
  }

  disconnect() {
    if (this.hasResultsFrameTarget && this.frameLoadHandler) {
      this.resultsFrameTarget.removeEventListener("turbo:frame-load", this.frameLoadHandler)
    }
  }

  handleFrameLoad(event) {
    // Update the browser URL to match the frame's src after navigation
    const frameSrc = this.resultsFrameTarget.getAttribute("src")
    if (frameSrc && frameSrc !== window.location.href) {
      history.pushState({}, "", frameSrc)
    }
  }

  filterChanged(event) {
    const changedFilter = event.target.dataset.filterName
    const form = this.filterFormTarget

    // Hide validation error when client is selected
    if (changedFilter === "client") this.hideValidationError()

    // Clear dependent filters when parent changes
    this.clearDependentFilters(changedFilter)

    // Build URL with current filter values
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    params.set("changed_filter", changedFilter)

    const url = `/distribution_report/filter_options?${params.toString()}`

    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html"
      }
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)

      // Enable/disable filters after options have loaded
      if (changedFilter === "client") {
        const hasClient = event.target.value !== ""
        if (this.hasClientHintTarget) this.clientHintTarget.classList.toggle("hidden", hasClient)
        if (this.hasFiltersWrapperTarget) {
          this.filtersWrapperTarget.classList.toggle("opacity-40", !hasClient)
          this.filtersWrapperTarget.classList.toggle("pointer-events-none", !hasClient)
          this.filtersWrapperTarget.classList.toggle("select-none", !hasClient)
        }
      }
    })
  }

  generateReport(event) {
    event.preventDefault()
    const clientSelect = this.filterFormTarget.querySelector('[name="client_id"]')
    if (!clientSelect || !clientSelect.value) {
      this.showValidationError("Please select a client before generating the report.")
      return
    }
    this.hideValidationError()
    this.filterFormTarget.requestSubmit()
  }

  showValidationError(message) {
    if (!this.hasValidationMessageTarget) return
    this.validationMessageTarget.textContent = message
    this.validationMessageTarget.classList.remove("hidden")
  }

  hideValidationError() {
    if (!this.hasValidationMessageTarget) return
    this.validationMessageTarget.classList.add("hidden")
  }

  // Stage the per-page value on the filter form so it's applied next time the
  // user clicks Generate Report. Does NOT submit the form -- the report only
  // regenerates on explicit Generate Report clicks.
  changePerPage(event) {
    const form = this.filterFormTarget
    let perPageInput = form.querySelector('[name="per_page"]')
    if (!perPageInput) {
      perPageInput = document.createElement("input")
      perPageInput.type = "hidden"
      perPageInput.name = "per_page"
      form.appendChild(perPageInput)
    }
    perPageInput.value = event.target.value

    // Reset to page 1 so the staged per_page applies from the first page
    let pageInput = form.querySelector('[name="page"]')
    if (pageInput) pageInput.value = "1"
  }

  clearDependentFilters(changedFilter) {
    const hierarchy = {
      "client": ["brand_id", "ph1_id", "ph2_id", "ph3_id", "retailer_id", "banner_id", "place_owner_id[]", "tasker_id[]", "place_id[]", "sku[]"],
      "brand": ["sku[]"],
      "ph1": ["ph2_id", "ph3_id", "sku[]"],
      "ph2": ["ph3_id", "sku[]"],
      "ph3": ["sku[]"],
      "retailer": ["banner_id", "place_owner_id[]", "place_id[]"],
      "banner": ["place_id[]"],
      "place_state": ["place_id[]"]
    }

    const toClear = hierarchy[changedFilter] || []
    toClear.forEach(fieldName => {
      const field = this.filterFormTarget.querySelector(`[name="${fieldName}"]`)
      if (field) {
        if (field.multiple) {
          Array.from(field.options).forEach(opt => opt.selected = false)
          const slimController = this.application.getControllerForElementAndIdentifier(field, "slim")
          if (slimController && slimController.select) {
            slimController.select.setSelected([])
          }
        } else {
          field.value = ""
        }
      }
    })
  }
}
